import { useOpenClose } from 'core/hooks/useOpenClose';
import { BorderedContainer } from '../../../common/BorderedContainer';
import {
  convertNumberToString,
  convertToPresenceAbsence,
  formatDateTime,
  formatTime,
} from '../../../common/converters';
import { SectionTitle } from '../../../common/SectionTitle';
import { InfoDisplay } from '../../common/InfoDisplay';
import { AccordionToggle } from '../common/AccordionToggle';
import { AccordionToggleContent } from '../common/AccordionToggleContent';
import { IncidentDetailPatient } from 'sections/niigata/features/IncidentDetail/useIncidentDetailForNiigata';

type Props = {
  /** 応急処置リスト。 */
  treatments: IncidentDetailPatient['treatments'];
};

/** 応急処置のコンポーネント。 */
export const Treatments = ({ treatments }: Props) => {
  return (
    <BorderedContainer>
      <div className="flex flex-col gap-[24px]">
        <SectionTitle text="応急処置" />
        <div className="flex flex-col gap-[8px]">
          {treatments.length > 0 &&
            treatments.map(treatment => (
              <Item key={treatment.treatmentId} treatment={treatment} />
            ))}
        </div>
      </div>
    </BorderedContainer>
  );
};

type ItemProps = {
  /** 応急処置。 */
  treatment: IncidentDetailPatient['treatments'][number];
};

const Item = ({ treatment }: ItemProps) => {
  const { isOpen, handleOpen, handleClose } = useOpenClose();

  return (
    <>
      <AccordionToggle
        isOpen={isOpen}
        onOpen={handleOpen}
        onClose={handleClose}
      >
        <p className="text-[16px] noto-sans-jp-medium text-textPrimary">
          {treatment.data.recordedAt
            ? formatTime(treatment.data.recordedAt)
            : '時間未指定'}
        </p>
      </AccordionToggle>
      <AccordionToggleContent isOpen={isOpen}>
        <div className="flex flex-wrap gap-x-[40px] gap-y-[24px] xl:gap-x-[96px]">
          <InfoDisplay
            title="記録日時"
            content={formatDateTime(treatment.data.recordedAt)}
          />
          <InfoDisplay
            title="止血"
            content={convertToPresenceAbsence(treatment.data.hemostasis)}
          />
          <InfoDisplay
            title="酸素投与"
            content={convertToPresenceAbsence(treatment.data.o2)}
          />
          <InfoDisplay
            title="心配蘇生"
            content={convertToPresenceAbsence(
              treatment.data.cardiopulmonaryResuscitation
            )}
          />
          <InfoDisplay
            title="除細動"
            content={convertToPresenceAbsence(treatment.data.defibrillation)}
          />
          <InfoDisplay
            title="除細動の回数"
            content={
              treatment.data.defibrillationCount
                ? `${convertNumberToString(treatment.data.defibrillationCount)} 回`
                : ''
            }
          />
        </div>
        <div className="flex flex-wrap gap-x-[40px] gap-y-[24px] xl:gap-x-[96px]">
          <InfoDisplay
            title="軌道確保"
            content={convertToPresenceAbsence(
              treatment.data.airwayEstablishment
            )}
          />
          <InfoDisplay
            title="挿管器具"
            content={treatment.data.intubationDevice ?? ''}
          />
          <InfoDisplay
            title="静脈路確保"
            content={convertToPresenceAbsence(
              treatment.data.ivAccessEstablishment
            )}
          />
          <InfoDisplay
            title="静脈路確保のタイミング"
            content={treatment.data.ivAccessTiming ?? ''}
          />
        </div>
        <div className="flex flex-wrap gap-x-[40px] gap-y-[24px] xl:gap-x-[96px]">
          <InfoDisplay
            title="血糖測定"
            content={convertToPresenceAbsence(treatment.data.glu)}
          />
          <InfoDisplay
            title="血糖値"
            content={
              treatment.data.gluValue
                ? `${convertNumberToString(treatment.data.gluValue)} mg/dl`
                : ''
            }
          />
          <InfoDisplay
            title="薬剤投与"
            content={convertToPresenceAbsence(
              treatment.data.medicationAdministration
            )}
          />
          <InfoDisplay
            title="投与薬剤"
            content={treatment.data.administeredMedication ?? ''}
          />
          <InfoDisplay
            title="投与回数"
            content={
              treatment.data.administrationCount
                ? `${convertNumberToString(treatment.data.administrationCount)} 回`
                : ''
            }
          />
        </div>
        <InfoDisplay title="その他" content={treatment.data.comment ?? ''} />
      </AccordionToggleContent>
    </>
  );
};
